import { useState } from 'react'
import {
  Modal,
  Stack,
  Select,
  NumberInput,
  Group,
  Button,
  Alert,
  Code,
  Textarea,
  CopyButton,
  Tooltip,
  ActionIcon,
} from '@mantine/core'
import { IconCheck, IconCopy, IconDownload } from '@tabler/icons-react'
import { connectorsApi, type CreateConnectorResponse } from '@/api/connectors'
import { useMutation } from '@tanstack/react-query'

export interface TenantOption {
  id: number
  name: string
}

export interface EmitCertModalProps {
  opened: boolean
  onClose: () => void
  tenants: TenantOption[]
  onIssued: () => void
}

export default function EmitCertModal({
  opened,
  onClose,
  tenants,
  onIssued,
}: EmitCertModalProps) {
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [environment, setEnvironment] = useState<string | null>('QA')
  const [validityDays, setValidityDays] = useState<number>(30)
  const [issued, setIssued] = useState<CreateConnectorResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: () =>
      connectorsApi.create({
        tenant_id: Number(tenantId),
        environment: environment as 'DEV' | 'QA' | 'HML' | 'PRD',
        validity_days: validityDays,
      }),
    onSuccess: (data) => {
      setIssued(data)
      onIssued()
      setError(null)
    },
    onError: (e: unknown) => {
      const msg = (e as { message?: string })?.message ?? 'falha ao emitir cert'
      setError(msg)
    },
  })

  const reset = () => {
    setTenantId(null)
    setEnvironment('QA')
    setValidityDays(30)
    setIssued(null)
    setError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const downloadBundle = () => {
    if (!issued) return
    // Bundle simples: cria 3 arquivos via Blob e Link click
    // Em prod ideal seria .tar.gz; no MVP fazemos zip via JSZip ou
    // simplesmente 3 downloads sequenciais.
    triggerDownload('cert.pem', issued.cert_pem)
    triggerDownload('key.pem', issued.key_pem)
    triggerDownload('docker-compose.yml', issued.docker_compose_yaml)
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Emitir certificado de connector"
      size="xl"
      closeOnClickOutside={false}
    >
      <Stack>
        {!issued && (
          <>
            <Select
              label="Tenant"
              placeholder="Selecione"
              data={tenants.map((t) => ({ value: String(t.id), label: t.name }))}
              value={tenantId}
              onChange={setTenantId}
              required
              data-testid="connector-tenant-select"
            />
            <Select
              label="Ambiente"
              data={['DEV', 'QA', 'HML', 'PRD']}
              value={environment}
              onChange={setEnvironment}
              required
              data-testid="connector-env-select"
            />
            <NumberInput
              label="Validade (dias)"
              min={1}
              max={365}
              value={validityDays}
              onChange={(v) => setValidityDays(Number(v) || 30)}
            />
            {error && (
              <Alert color="red" title="Erro">
                {error}
              </Alert>
            )}
            <Group justify="end">
              <Button variant="default" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={() => createMutation.mutate()}
                loading={createMutation.isPending}
                disabled={!tenantId || !environment}
                data-testid="emit-cert-submit"
              >
                Emitir
              </Button>
            </Group>
          </>
        )}

        {issued && (
          <>
            <Alert color="green" title="Cert emitido com sucesso">
              Salve estes arquivos com segurança — a chave privada não sera
              mostrada novamente.
            </Alert>
            <CertField label="Fingerprint (SHA-256)" value={issued.fingerprint} />
            <CertField label="cert.pem" value={issued.cert_pem} multiline />
            <CertField label="key.pem" value={issued.key_pem} multiline />
            <CertField
              label="docker-compose.yml"
              value={issued.docker_compose_yaml}
              multiline
            />
            <Group justify="end">
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={downloadBundle}
              >
                Baixar bundle (.pem + compose)
              </Button>
              <Button onClick={handleClose}>Fechar</Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}

function CertField({
  label,
  value,
  multiline = false,
}: {
  label: string
  value: string
  multiline?: boolean
}) {
  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <Code>{label}</Code>
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copiado' : 'Copiar'}>
              <ActionIcon
                variant="subtle"
                onClick={copy}
                aria-label={`Copiar ${label}`}
              >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
      {multiline ? (
        <Textarea
          value={value}
          readOnly
          autosize
          minRows={3}
          maxRows={10}
          styles={{ input: { fontFamily: 'monospace', fontSize: '0.8em' } }}
        />
      ) : (
        <Code block>{value}</Code>
      )}
    </Stack>
  )
}

function triggerDownload(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
